import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import styles from './DescriptionEditDialog.module.css';

// Correct an OCR-misread transaction description. The scanned text is never
// lost — the server keeps it alongside the correction — so this dialog always
// shows it and offers a one-click revert. Saving re-runs categorization with the
// corrected text (category / flag / merchant a person already set are kept).
export const DESCRIPTION_MAX_LENGTH = 500; // must match backend TRANSACTION_DESCRIPTION_MAX_LENGTH

const DescriptionEditDialog = ({
  open,
  onClose,
  onSave,
  description = '',
  originalDescription = null, // present only when the description was already edited
}) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (open) {
      setValue(description || '');
    }
  }, [open, description]);

  const scanned = originalDescription ?? description;
  const isEdited = originalDescription != null;
  const trimmed = value.trim();
  const overLimit = value.length > DESCRIPTION_MAX_LENGTH;
  const unchanged = trimmed === (description || '').trim();

  const handleSave = useCallback(() => onSave(trimmed), [onSave, trimmed]);
  const handleRevert = useCallback(() => onSave(''), [onSave]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Description</DialogTitle>
      <DialogContent dividers>
        <div className={styles.scanned}>
          <span className={styles.scannedLabel}>Scanned text</span>
          <span className={styles.scannedValue}>{scanned || '(blank)'}</span>
        </div>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          label="Description"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={overLimit}
          helperText={
            overLimit
              ? `Description is too long (${value.length}/${DESCRIPTION_MAX_LENGTH} characters).`
              : `${value.length}/${DESCRIPTION_MAX_LENGTH}`
          }
        />
        <p className={styles.hint}>
          Saving re-runs categorization with the corrected description. Any category,
          flag, or merchant a person already set stays as it is.
        </p>
      </DialogContent>
      <DialogActions>
        {isEdited && (
          <Button onClick={handleRevert} className={styles.revert}>
            Revert to Scanned Text
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={overLimit || !trimmed || unchanged}>
          Save Description
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DescriptionEditDialog;
